// @ts-strict-ignore
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ProjectModel } from '@project/model/project.model';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaUsageVO, ConnectionAreaVO, ConnectionVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Line } from '@3d-scene/model/line';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Injectable } from '@angular/core';
import { ConnectionFactory } from '@3d-scene/model3d/connection.factory';
import { MeshComponentUtils } from '@3d-scene/model3d/util/mesh-component-utils';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ArrayUtils } from '@shared/util/array-utils';

@Injectable({ providedIn: 'root' })
export class CreateLabelingHelper {
  private static readonly STUB_CONNECTION_NAME: string = 'cn_';

  constructor(
    private mountingRailHelper: MountingRailHelper,
    private containerService: ContainerService,
    private meshComponentUtils: MeshComponentUtils,
    private connectionFactory: ConnectionFactory,
    private connectionInfoService: ConnectionInfoService,
    private labelingUtils: LabelingUtils,
    private articleConnectionService: ArticleConnectionService,
    private projectModel: ProjectModel,
  ) {}

  public createTextMeshForDynamicConnections(
    givingArticleContainer: ArticleContainer,
    givingAreaMesh: Connection,
    createTextures: boolean,
    isInvertedDirection: boolean
  ): boolean {
    if (!givingArticleContainer || !givingAreaMesh) {
      return false;
    }
    let connectionsAdded: boolean = false;
    const nameIndex: number = this.getNameIndex(givingArticleContainer);
    const articlesGUIDs: string[] = givingArticleContainer.properties.getValue(ArticleContainerPropertyDef.ARTICLES_GUIDS_BETWEEN_ADAPTERS);
    if (articlesGUIDs) {
      connectionsAdded = this.createTextMeshForDynamicConnectionsByAdapters(
        givingArticleContainer,
        givingAreaMesh,
        articlesGUIDs,
        nameIndex,
        createTextures,
        isInvertedDirection
      );
    } else {
      connectionsAdded = this.createTextMeshForDynamicConnectionsByArticleUsages(
        givingArticleContainer,
        givingAreaMesh,
        nameIndex,
        createTextures,
        isInvertedDirection
      );
    }
    return connectionsAdded;
  }

  private getNameIndex(givingArticle: ArticleContainer): number {
    const nameIndex: number = this.getCurrentMinNumberConnectionInLabeling(givingArticle.articleUsage);
    return NumberUtils.isNumber(nameIndex) ? nameIndex : 1;
  }

  private getCurrentMinNumberConnectionInLabeling(articleUsage: ArticleUsageVO): number {
    let minElement: number = NaN;
    for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
      const currentNumber: number = this.getConnectionNumber(textureAreaUsage.identity.metaElementName);
      if (!NumberUtils.isNumber(minElement) || minElement > currentNumber) {
        minElement = currentNumber;
      }
    }
    return minElement;
  }

  private getConnectionNumber(metaElementName: string): number {
    const i: number = metaElementName.search(/(\d){1,50}$/);
    return parseFloat(metaElementName.substring(i));
  }

  private createTextMeshForDynamicConnectionsByAdapters(
    givingArticleContainer: ArticleContainer,
    givingAreaMesh: Connection,
    articlesGUIDs: string[],
    nameIndex: number,
    createTextures: boolean,
    isInvertedDirection: boolean
  ): boolean {
    const insertArticleHelper: InsertArticleHelper = this.getInsertArticleHelper();
    let articleLine: Line = null;
    let connectionsAdded: boolean = false;
    let isNegativeOffset: boolean = this.isLabelingWithNegativeOffset(givingArticleContainer);
    if (isInvertedDirection) {
      isNegativeOffset = !isNegativeOffset;
    }

    const articleOnRailGuidToDynamicConnectionMap: Dictionary<string, ConnectionAreaUsageVO> = this.getArticleGuidOnRailToDynamicConnectionMap(
      ArticleUsageUtils.connectionAreaUsages(givingArticleContainer.articleUsage),
      articlesGUIDs
    );

    const startConnectPoint: Vector3 = this.getStartAdapterConnectionPoint(givingArticleContainer, insertArticleHelper);
    if (!startConnectPoint) {
      return false;
    }

    // We have to iterate in the following order because of already existing projects:
    // 0 (first), length - 1 (last), 1, 2, ..., length - 2
    let currentNameIndex = nameIndex;
    const ids = articlesGUIDs.length == 1 ? [0] : [0, articlesGUIDs.length - 1, ...ArrayUtils.range(1, articlesGUIDs.length - 2)];

    for (const i of ids) {
      const currentArticleGuid = articlesGUIDs[i];
      const newName = MetaElementVO.TEXTURE_PREFIX + currentNameIndex.toString();
      let processedContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(currentArticleGuid);
      if (!processedContainer) {
        return false;
      }
      this.updateConnectionName(
        givingArticleContainer,
        articleOnRailGuidToDynamicConnectionMap.getValue(currentArticleGuid),
        MetaElementVO.CONNECTION_PREFIX + currentNameIndex.toString()
      );
      articleLine = insertArticleHelper.getArticleLine(processedContainer);
      if (i == 0) {
        articleLine = new Line(
          MathUtils.closestPointOnLine(
            insertArticleHelper.baseLine.start,
            insertArticleHelper.baseLine.end,
            givingArticleContainer.sceneTransformBounds.max
          ),
          articleLine.end
        );
      }
      if (i == articlesGUIDs.length - 1) {
        articleLine = new Line(
          articleLine.start,
          MathUtils.closestPointOnLine(
            insertArticleHelper.baseLine.start,
            insertArticleHelper.baseLine.end,
            givingArticleContainer.sceneTransformBounds.min
          )
        );
      }
      if (i > 0 && i < articlesGUIDs.length - 1) {
        this.updatePropertiesByParent(givingArticleContainer.articleUsage, processedContainer, newName);
      }

      const middlePoint = this.getMiddlePointByLine(articleLine);
      const offset = this.getOffsetBetweenPoints(startConnectPoint, middlePoint, isNegativeOffset);
      this.meshComponentUtils.addTextMeshToContainer(
        givingArticleContainer,
        givingAreaMesh,
        newName,
        offset,
        articleLine.length,
        createTextures
      );
      connectionsAdded = true;
      currentNameIndex++;
    }

    return connectionsAdded;
  }

  private updatePropertiesByParent(articleUsage: ArticleUsageVO, parentArticleContainer: ArticleContainer, connectionName: string): void {
    if (!parentArticleContainer || !parentArticleContainer.article || !parentArticleContainer.articleUsage) {
      return;
    }

    const parentSpacing: number = LabelingUtils.getSpacingByArticle(parentArticleContainer.article, connectionName, false);
    ArticleUsageUtils.setDefinedMetaElementInfoPropertyFloatValue(
      articleUsage,
      connectionName,
      DefinedMetaElementInfoPropertyDef.SPACING,
      parentSpacing
    );
    ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
      articleUsage,
      connectionName,
      DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID,
      parentArticleContainer.articleUsage.id
    );
    ReferenceArticleUsageUtils.addForwardReferenceArticleUsageId(parentArticleContainer.articleUsage, articleUsage.id);
  }

  private updateConnectionName(givingArticle: ArticleContainer, connectionAreaUsage: ConnectionAreaUsageVO, newConnectionName: string): void {
    if (!connectionAreaUsage) {
      return;
    }

    const connectionName: string = connectionAreaUsage.identity.metaElementName;
    if (newConnectionName === connectionName) {
      return;
    }

    let stubConnectionAreaUsage: ConnectionAreaUsageVO = null;

    if (newConnectionName !== CreateLabelingHelper.STUB_CONNECTION_NAME) {
      let stubConnectionAreaUsage = ArticleUsageUtils.findConnectionAreaUsageByName(givingArticle.articleUsage, newConnectionName);
      if(stubConnectionAreaUsage) {
        this.updateConnectionName(givingArticle, stubConnectionAreaUsage, CreateLabelingHelper.STUB_CONNECTION_NAME);
      }
    }

    connectionAreaUsage.identity.metaElementName = newConnectionName;
    for (const givingAreaId of connectionAreaUsage.connection.givingAreaIds) {
      if (givingAreaId.articleUsageId === connectionAreaUsage.identity.articleUsageId && connectionName === givingAreaId.metaElementName) {
        givingAreaId.metaElementName = newConnectionName;
      }
    }

    this.updateMetaElementConnectionNames(givingArticle, connectionName, newConnectionName);

    if (stubConnectionAreaUsage) {
      this.updateConnectionName(givingArticle, stubConnectionAreaUsage, connectionName);
    }
  }

  private updateMetaElementConnectionNames(givingArticle: ArticleContainer, connectionName: string, newConnectionName: string) {
    for (const metaElement of givingArticle.article.metaElements) {
      if (metaElement instanceof ConnectionAreaVO && metaElement.name === connectionName) {
        metaElement.name = newConnectionName;
        break;
      }
    }

    const connections: Connection[] = givingArticle.connections;
    for (const connection of connections) {
      if (connection.id === connectionName) {
        const updatedConnection: Connection = this.connectionFactory.getNewInstance(givingArticle, connection.mesh, connection.type);
        const connectionIndex: number = connections.indexOf(connection);
        connections.splice(connectionIndex, 1, updatedConnection);
        break;
      }
    }

    const projectModel: ProjectModel = givingArticle.projectModel;
    const info: ArticleInfoVO = ArticleUtils.findArticleInfoByGuid(givingArticle.articleGUID, projectModel.currentProject);
    if (info) {
      for (const metaElementInfo of info.metaElements) {
        if (metaElementInfo.name === connectionName) {
          metaElementInfo.name = newConnectionName;
          break;
        }
      }
    }
  }

  private getStartAdapterConnectionPoint(givingArticle: ArticleContainer, insertArticleHelper: InsertArticleHelper): Vector3 {
    let startPoint: Vector3 = null;
    for (const connection of ArticleUsageUtils.connections(givingArticle.articleUsage)) {
      const acceptingConnection: Connection = this.getFirstAcceptingConnection(givingArticle.articleGUID, connection);
      if (acceptingConnection) {
        const acceptingRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(acceptingConnection);
        const connectionLine: Line = this.calculateLineForDynamicConnection(acceptingRectangle);
        const connectionMiddlePoint: Vector3 = this.getMiddlePointByLine(connectionLine);
        if (!startPoint) {
          startPoint = connectionMiddlePoint;
        } else {
          if (startPoint.x < connectionMiddlePoint.x) {
            startPoint = connectionMiddlePoint;
          }
        }
      }
    }

    return !startPoint ? null : MathUtils.closestPointOnLine(insertArticleHelper.baseLine.start, insertArticleHelper.baseLine.end, startPoint);
  }

  private getMiddlePointByLine(articleLine: Line): Vector3 {
    if (!articleLine) {
      return null;
    }
    return MathUtils.calculateMiddlePoint(articleLine.start, articleLine.end);
  }

  private calculateLineForDynamicConnection(acceptingRectangle: Rectangle3D): Line {
    if (!acceptingRectangle) {
      return null;
    }
    const insertArticleHelper: InsertArticleHelper = this.getInsertArticleHelper();

    let currentStartPositionConnection: Vector3 = null;
    let currentEndPositionConnection: Vector3 = null;
    if (acceptingRectangle.point1.x > acceptingRectangle.point4.x) {
      currentEndPositionConnection = MathUtils.closestPointOnLine(
        insertArticleHelper.baseLine.start,
        insertArticleHelper.baseLine.end,
        acceptingRectangle.point4
      );
      currentStartPositionConnection = MathUtils.closestPointOnLine(
        insertArticleHelper.baseLine.start,
        insertArticleHelper.baseLine.end,
        acceptingRectangle.point1
      );
    } else {
      currentStartPositionConnection = MathUtils.closestPointOnLine(
        insertArticleHelper.baseLine.start,
        insertArticleHelper.baseLine.end,
        acceptingRectangle.point4
      );
      currentEndPositionConnection = MathUtils.closestPointOnLine(
        insertArticleHelper.baseLine.start,
        insertArticleHelper.baseLine.end,
        acceptingRectangle.point1
      );
    }
    return new Line(currentStartPositionConnection, currentEndPositionConnection);
  }

  private getArticleGuidOnRailToDynamicConnectionMap(
    connectionAreaUsages: ConnectionAreaUsageVO[],
    articlesGUIDs: string[]
  ): Dictionary<string, ConnectionAreaUsageVO> {
    const articleOnRailGuidToDynamicConnectionMap: Dictionary<string, ConnectionAreaUsageVO> = new Dictionary<string, ConnectionAreaUsageVO>();
    const freeArticleGuids: string[] = articlesGUIDs.concat();

    for (const connectionAreaUsage of connectionAreaUsages) {
      const backwardConnectedArticlesOnRail: ArticleContainer[] =
        this.articleConnectionService.getBackwardConnectedArticlesOnRailByConnectionArea(connectionAreaUsage);
      // TODO: (from flex) processing multiple backward containers
      const processedContainer: ArticleContainer = this.getFirstArticleOnRailBetweenAdapters(backwardConnectedArticlesOnRail, freeArticleGuids);
      if (!processedContainer) {
        continue;
      }
      articleOnRailGuidToDynamicConnectionMap.setValue(processedContainer.articleGUID, connectionAreaUsage);
      freeArticleGuids.splice(freeArticleGuids.indexOf(processedContainer.articleGUID), 1);
    }

    return articleOnRailGuidToDynamicConnectionMap;
  }

  private getFirstArticleOnRailBetweenAdapters(articleContainers: ArticleContainer[], articleGuidBetweenAdapters: string[]): ArticleContainer {
    for (const articleContainer of articleContainers) {
      if (articleGuidBetweenAdapters.indexOf(articleContainer.articleGUID) !== -1) {
        return articleContainer;
      }
    }
    return null;
  }

  private isLabelingWithNegativeOffset(givingArticle: ArticleContainer): boolean {
    for (const connection of ArticleUsageUtils.connections(givingArticle.articleUsage)) {
      const acceptingConnection: Connection = this.getFirstAcceptingConnection(givingArticle.articleGUID, connection);
      if (acceptingConnection) {
        const acceptingRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(acceptingConnection);
        // TODO: (from flex) new cases might require that offset should be negated, it's then not sufficient to check just x-coordinates
        return acceptingRectangle.point1.x > acceptingRectangle.point4.x;
      }
    }
    return false;
  }

  private getFirstAcceptingConnection(givingArticleGUID: string, connection: ConnectionVO): Connection {
    let acceptingConnection: Connection = null;
    for (const identity of connection.givingAreaIds) {
      if (identity.articleUsageId === givingArticleGUID) {
        acceptingConnection = this.connectionInfoService.findConnectionByIdentity(connection.acceptingAreaId);
        if (acceptingConnection) {
          return acceptingConnection;
        }
      }
    }
    return acceptingConnection;
  }

  public getInsertArticleHelper(): InsertArticleHelper {
    return this.mountingRailHelper.getInsertArticleHelper();
  }

  private createTextMeshForDynamicConnectionsByArticleUsages(
    givingArticleContainer: ArticleContainer,
    givingAreaMesh: Connection,
    nameIndex: number,
    createTextures: boolean,
    isInvertedDirection: boolean
  ): boolean {
    let connectionsAdded: boolean = false;
    let startConnectPoint: Vector3 = null;

    let currentNameIndex: number = nameIndex;
    let newName: string; // = MetaElementVO.TEXTURE_PREFIX + nameIndex.toString();
    let offset: Vector3 = new Vector3();
    let middlePoint: Vector3 = null;

    for (const connection of ArticleUsageUtils.connections(givingArticleContainer.articleUsage)) {
      const acceptingConnection: Connection = this.getFirstAcceptingConnectionByConnection(givingArticleContainer.articleGUID, connection);
      if (acceptingConnection) {
        const acceptingRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(acceptingConnection);
        middlePoint = MathUtils.calculateMiddlePointByRectangle(acceptingRectangle);
        if (!startConnectPoint) {
          startConnectPoint = middlePoint;
        }
        offset = this.getOffsetBetweenPoints(middlePoint, startConnectPoint, false);

        newName = MetaElementVO.TEXTURE_PREFIX + currentNameIndex.toString();
        this.meshComponentUtils.addTextMeshToContainer(
          givingArticleContainer,
          givingAreaMesh,
          newName,
          offset,
          acceptingRectangle.width,
          createTextures
        );
        connectionsAdded = true;
        currentNameIndex++;
      }
    }
    return connectionsAdded;
  }

  private getFirstAcceptingConnectionByConnection(givingArticleGUID: string, connection: ConnectionVO): Connection {
    let acceptingConnection: Connection = null;
    for (const identity of connection.givingAreaIds) {
      if (identity.articleUsageId === givingArticleGUID) {
        acceptingConnection = this.connectionInfoService.findConnectionByIdentity(connection.acceptingAreaId);
        if (acceptingConnection) {
          return acceptingConnection;
        }
      }
    }
    return acceptingConnection;
  }

  private getOffsetBetweenPoints(firstPoint: Vector3, lastPoint: Vector3, isNegativeOffset: boolean): Vector3 {
    const offset: Vector3 = firstPoint.clone().sub(lastPoint);
    if (isNegativeOffset) {
      offset.negate();
    }
    return offset;
  }

  public collectArticlesBetweenContainers(firstAdapterContainer: ArticleContainer, lastAdapterContainer: ArticleContainer, includeInputContainers: boolean): ArticleContainer[] {
    const insertArticleHelper: InsertArticleHelper = this.getInsertArticleHelper();
    const baseLine: AdvancedLine = insertArticleHelper.baseLine;

    const firstAdapterCube: Connection = ConnectionInfoUtils.getFirstGivingConnection(firstAdapterContainer);
    const firstMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(firstAdapterCube.compositionRectangle);

    const lastAdapterCube: Connection = ConnectionInfoUtils.getFirstGivingConnection(lastAdapterContainer);
    const lastMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(lastAdapterCube.compositionRectangle);

    const firstPoint: Vector3 = MathUtils.closestPointOnLine(baseLine.start, baseLine.end, firstMiddlePoint);
    const lastPoint: Vector3 = MathUtils.closestPointOnLine(baseLine.start, baseLine.end, lastMiddlePoint);
    const collectedArticles: ArticleContainer[] = insertArticleHelper.getConnectedArticlesFromLine(new AdvancedLine(firstPoint, lastPoint));

    if(!includeInputContainers) {
      ArrayUtils.removeAllItemsInArray(collectedArticles, [firstAdapterContainer, lastAdapterContainer])
    }

    return collectedArticles;
  }

  /* createTextMeshForDynamicConnectionsByAdapters iterates textures in the order 0,lastElement,1,2,3....
  * This method fixes this order for textures that were added in ascending order  */
  public correctTextureSequenceForAdapters() {
    const labelsOnAdapters = this.containerService.enumerateArticleContainersInGlobal()
      .filter((articleContainer) => this.containerService.isLabelOnAdapter(articleContainer));

    for (const label of labelsOnAdapters) {
      if(label.textConnections.length > 1) {
        this.correctTextureAreaUsageOrder(label)
        this.correctMetaElementOrder(label);
        label.refreshTextureConnections();
      }

    }
  }


  private correctTextureAreaUsageOrder(articleContainer: ArticleContainer) {

    // Only do this if there are more than two parents
    const backwardConnectionAreas = ArticleUsageUtils.backwardConnectionAreaUsages(articleContainer.articleUsage);
    if (new Set(backwardConnectionAreas.map(c => c.connection.acceptingAreaId.articleUsageId)).size <= 1) {
      return;
    }

    const renamingMap = new Map<string, string>();

    const textureElements = ArticleUtils.getTextureElements(articleContainer.article);

    // See createTextMeshForDynamicConnectionsByAdapters
    // First element stays the same
    for (let index = 1; index < textureElements.length; index++) {

      const textureElement = textureElements[index];
      // Last element gets the name of the second element
      if (index === textureElements.length - 1) {
        renamingMap.set(textureElement.name, MetaElementVO.TEXTURE_PREFIX + '2');
        continue;
      }
      // Rest of the elements + 1
      renamingMap.set(textureElement.name, MetaElementVO.TEXTURE_PREFIX + (index + 2).toString());
    }

    // Second, do all the renaming.
    for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleContainer.articleUsage)) {
      // Some identities might be shared, so we create a new one
      textureAreaUsage.identity = this.toRenamedIdentity(textureAreaUsage.identity, renamingMap);
      textureAreaUsage.previousMergedUsageId = this.toRenamedIdentity(textureAreaUsage.previousMergedUsageId, renamingMap);
      textureAreaUsage.nextMergedUsageId = this.toRenamedIdentity(textureAreaUsage.nextMergedUsageId, renamingMap);
    }
  }


  private correctMetaElementOrder(articleContainer: ArticleContainer) {
    articleContainer.article.metaElements
      .sort((me1, me2) => me1.name.localeCompare(me2.name, undefined, {numeric: true, sensitivity: 'base'}));
    const articleInfo = this.projectModel.currentProject.dynamicArticlesInfo.find(info => info.guid === articleContainer.articleGUID);
    if (articleInfo) {
      articleInfo.metaElements.sort((me1, me2) => me1.name.localeCompare(me2.name, undefined, {numeric: true, sensitivity: 'base'}));
    }
  }

  private toRenamedIdentity(identity: MetaElementUsageIdentityVO, renamingMap: Map<string, string>): MetaElementUsageIdentityVO {
    if (!identity) {
      return identity;
    }
    const newName = renamingMap.get(identity.metaElementName);
    return newName
      ? MetaElementUsageIdentityVO.create(identity.articleUsageId, newName) // renamed -> create new identity
      : identity; // no change -> keep original
  }

}
